/**
 * Expression AST evaluator.
 *
 * Walks AST nodes emitted by the compiler and evaluates them against
 * a Blackboard. Implements balanced ternary semantics for logic ops.
 * Supports all node kinds including `call` and `visits`.
 */

import { Blackboard, untagValue, tagValue, type TaggedValue } from "./Blackboard";
import { Ternary, T, U, F } from "./Ternary";
import type { GameClock } from "./GameClock";

/** AST node as emitted by the compiler. */
export interface AstNode {
  kind: string;
  v?: number | string | boolean;
  n?: string;
  /** path node: root variable name */
  root?: string;
  /** path node: list of string (dict key) or number (list index) segments */
  parts?: (string | number)[];
  x?: AstNode;
  l?: AstNode;
  r?: AstNode;
  args?: AstNode[];
  scene?: string;
  items?: AstNode[];
  entries?: Array<{ key: string; value: AstNode }>;
  /** collection predicate nodes (any/all/count): the collection expression */
  collection?: AstNode;
  /** collection predicate nodes (any/all/count): the per-element predicate */
  predicate?: AstNode;
}

/** Result of evaluation — either a number, string, boolean, Ternary, array, or object. */
export type EvalResult = number | string | boolean | Ternary | EvalResultList | EvalResultDict;
export interface EvalResultList extends Array<EvalResult> {}
export interface EvalResultDict { [key: string]: EvalResult }

/** Coerce an EvalResult to a number for arithmetic. */
function toNumber(v: EvalResult): number {
  if (typeof v === "number") return v;
  if (v instanceof Ternary) return v.toNumber();
  if (typeof v === "boolean") return v ? 1 : 0;
  if (typeof v === "string") {
    const n = Number(v);
    return Number.isNaN(n) ? 0 : n;
  }
  return 0;
}

/** Coerce an EvalResult to a Ternary for logic ops. */
function toTernary(v: EvalResult): Ternary {
  if (v instanceof Ternary) return v;
  if (typeof v === "boolean") return v ? T : F;
  if (typeof v === "number") return Ternary.from(v);
  return U;
}

/** Compare two EvalResults. Returns Ternary.
 *  If either is U, result is U (unknown propagation). */
function compare(
  l: EvalResult,
  r: EvalResult,
  op: (a: number, b: number) => boolean,
): Ternary {
  // U propagation: if either operand is Unknown, result is Unknown
  if (l instanceof Ternary && l.isUnknown) return U;
  if (r instanceof Ternary && r.isUnknown) return U;

  const ln = toNumber(l);
  const rn = toNumber(r);
  return op(ln, rn) ? T : F;
}

/** String equality comparison with U propagation. */
function compareEq(l: EvalResult, r: EvalResult): Ternary {
  if (l instanceof Ternary && l.isUnknown) return U;
  if (r instanceof Ternary && r.isUnknown) return U;

  // String comparison
  if (typeof l === "string" && typeof r === "string") {
    return l === r ? T : F;
  }
  // Ternary comparison
  if (l instanceof Ternary && r instanceof Ternary) {
    return l.equals(r) ? T : F;
  }
  // Numeric comparison
  const ln = toNumber(l);
  const rn = toNumber(r);
  return ln === rn ? T : F;
}

function compareNeq(l: EvalResult, r: EvalResult): Ternary {
  const eq = compareEq(l, r);
  return eq.not();
}

export interface VisitProvider {
  getVisits(scene: string): number;
}

export interface SetStatement {
  var: string;
  expr_ast: AstNode;
}

export interface IfStatement {
  kind: "if";
  cond: AstNode;
  /** T branch. */
  then: FunctionStatement[];
  /** U (Unknown) branch; falls through to `else` when absent. */
  unknown?: FunctionStatement[];
  /** F branch (also the home for lowered ~elif chains). */
  else?: FunctionStatement[];
}

export type FunctionStatement = SetStatement | IfStatement;

export interface FunctionDef {
  name: string;
  args: string[];
  dest: string;
  body_ast: AstNode | FunctionStatement[] | null;
  body_source?: string;
}

export interface FunctionProvider {
  functions: FunctionDef[];
}

let _visitProvider: VisitProvider | null = null;
let _functionProvider: FunctionProvider | null = null;

export function setVisitProvider(provider: VisitProvider | null): void {
  _visitProvider = provider;
}

export function setFunctionProvider(provider: FunctionProvider | null): void {
  _functionProvider = provider;
}

// --- Native time builtins (see design_docs/game_time_design_10JUN26.md §3.1) ---

let _gameClock: GameClock | null = null;

/** Wire the active GameClock so time builtins can resolve. Null disables them. */
export function setGameClock(clock: GameClock | null): void {
  _gameClock = clock;
}

/** Coerce the i-th call argument to a number. */
function arg(args: EvalResult[], i: number): number {
  return toNumber(args[i] ?? 0);
}

/** A weekday argument may be a name (string) or a 0-based index (number). */
function weekdayArg(v: EvalResult | undefined): number | string {
  return typeof v === "string" ? v : toNumber(v ?? 0);
}

type TimeBuiltin = (gc: GameClock, args: EvalResult[]) => EvalResult;

/**
 * Reserved time-builtin names, resolved natively before user-`~func` lookup.
 * This table is the single TS source of truth for the builtin name set
 * (the Python compiler keeps a matching list — see plan §8).
 */
const TIME_BUILTINS: Record<string, TimeBuiltin> = {
  // accessors
  hour: (gc) => gc.hour(),
  minute: (gc) => gc.minute(),
  day: (gc) => gc.day(),
  day_of_week: (gc) => gc.dayOfWeek(),
  weekday: (gc) => gc.weekday(),
  part_of_day: (gc) => gc.partOfDay(),
  time_str: (gc) => gc.timeStr(),
  month: (gc) => gc.month(),
  year: (gc) => gc.year(),
  clock: (gc) => gc.clock(),
  // tests (logical predicates)
  before: (gc, a) => gc.before(arg(a, 0), arg(a, 1)),
  after: (gc, a) => gc.after(arg(a, 0), arg(a, 1)),
  between: (gc, a) => gc.between(arg(a, 0), arg(a, 1), arg(a, 2), arg(a, 3)),
  is_weekday: (gc, a) => gc.isWeekday(weekdayArg(a[0])),
  on_or_after: (gc, a) => gc.onOrAfter(arg(a, 0), arg(a, 1), arg(a, 2)),
  // quantitative
  minutes_until: (gc, a) => gc.minutesUntil(arg(a, 0), arg(a, 1)),
};

/** The reserved builtin names (for tests / reservation checks). */
export const TIME_BUILTIN_NAMES: ReadonlySet<string> = new Set(Object.keys(TIME_BUILTINS));

// --- Native inventory builtins ---

type InventoryBuiltin = (bb: Blackboard, args: EvalResult[]) => EvalResult;

/**
 * Reserved inventory-builtin names, resolved natively before user-`~func`
 * lookup. Inventory state lives in the `_inventory` blackboard array (see
 * InventoryManager), so these read it directly — no provider needed.
 * The Python compiler keeps a matching list (see the cross-language drift test).
 */
const INVENTORY_BUILTINS: Record<string, InventoryBuiltin> = {
  // has_item(object_id) → T if the object is currently held, else F.
  has_item: (bb, args) => {
    const id = String(args[0] ?? "");
    const raw = bb.has("_inventory") ? untagValue(bb.get("_inventory")) : [];
    return Array.isArray(raw) && raw.map(String).includes(id) ? T : F;
  },
};

/** The reserved inventory-builtin names (for tests / reservation checks). */
export const INVENTORY_BUILTIN_NAMES: ReadonlySet<string> = new Set(Object.keys(INVENTORY_BUILTINS));

export function callFunctionImpl(
  bb: Blackboard,
  fnDef: FunctionDef,
  argValues: unknown[],
): void {
  if (!fnDef.body_ast) return;

  const bindings: Record<string, TaggedValue> = {};
  for (let i = 0; i < fnDef.args.length; i++) {
    bindings[fnDef.args[i]] = tagValue(argValues[i] ?? evaluate({ kind: "u" }, bb));
  }

  bb.pushScope(bindings);
  try {
    if (Array.isArray(fnDef.body_ast)) {
      execFuncStatements(fnDef.body_ast, bb);
    } else {
      bb.set(fnDef.dest, tagValue(evaluate(fnDef.body_ast, bb)));
    }
  } finally {
    bb.popScope();
  }
}

function execFuncStatements(stmts: FunctionStatement[], bb: Blackboard): void {
  for (const stmt of stmts) {
    if ("kind" in stmt && stmt.kind === "if") {
      // Conditions evaluate to a Ternary (comparisons) or a raw value;
      // normalize the same way the rest of the engine does. Ternary branch
      // selection mirrors Interpreter._execIf: T → then; U → unknown if present,
      // else fall through to else; F → else.
      const tern = Ternary.from(evaluate(stmt.cond, bb));
      if (tern.isTrue) {
        execFuncStatements(stmt.then, bb);
      } else if (tern.isUnknown && stmt.unknown && stmt.unknown.length > 0) {
        execFuncStatements(stmt.unknown, bb);
      } else if (stmt.else) {
        execFuncStatements(stmt.else, bb);
      }
    } else {
      const s = stmt as SetStatement;
      bb.set(s.var, tagValue(evaluate(s.expr_ast, bb)));
    }
  }
}

/**
 * Evaluate an expression AST against a Blackboard.
 *
 * @param node  The AST node to evaluate.
 * @param bb    The Blackboard providing variable values.
 * @returns     The evaluated result.
 */
export function evaluate(node: AstNode, bb: Blackboard): EvalResult {
  switch (node.kind) {
    // --- Literals ---
    case "num":
      return node.v as number;
    case "str":
      return node.v as string;
    case "bool":
      return node.v as boolean;
    case "t":
      return T;
    case "f":
      return F;
    case "u":
      return U;

    // --- Variable reference ---
    case "var": {
      const tv = bb.get(node.n!);
      return untagValue(tv);
    }

    // --- Dot-path reference ---
    case "path": {
      let val: unknown = untagValue(bb.get(node.root!));
      for (const seg of node.parts ?? []) {
        if (val === null || val === undefined) return U;
        if (typeof seg === "number") {
          if (!Array.isArray(val)) return U;
          val = (val as unknown[])[seg];
          if (val === undefined) return U;
        } else {
          if (typeof val !== "object" || Array.isArray(val) || val instanceof Ternary) return U;
          val = (val as Record<string, unknown>)[seg];
          if (val === undefined) return U;
        }
      }
      if (val === undefined || val === null) return U;
      return val as EvalResult;
    }

    // --- Implicit `it` binding (valid inside any/all/count predicates) ---
    case "it":
      return untagValue(bb.get("it"));

    // --- Collection predicates ---
    case "any": {
      const coll = evaluate(node.collection!, bb);
      if (!Array.isArray(coll)) return U;
      let sawU = false;
      for (const elem of coll as EvalResult[]) {
        bb.pushScope({ it: tagValue(elem) });
        const res = toTernary(evaluate(node.predicate!, bb));
        bb.popScope();
        if (res.isTrue) return T;
        if (res.isUnknown) sawU = true;
      }
      return sawU ? U : F;
    }

    case "all": {
      const coll = evaluate(node.collection!, bb);
      if (!Array.isArray(coll)) return U;
      let sawU = false;
      for (const elem of coll as EvalResult[]) {
        bb.pushScope({ it: tagValue(elem) });
        const res = toTernary(evaluate(node.predicate!, bb));
        bb.popScope();
        if (res.isFalse) return F;
        if (res.isUnknown) sawU = true;
      }
      return sawU ? U : T;
    }

    case "count": {
      const coll = evaluate(node.collection!, bb);
      if (!Array.isArray(coll)) return 0;
      let n = 0;
      for (const elem of coll as EvalResult[]) {
        bb.pushScope({ it: tagValue(elem) });
        const res = toTernary(evaluate(node.predicate!, bb));
        bb.popScope();
        if (res.isTrue) n++;
      }
      return n;
    }

    // --- Unary ---
    case "neg":
      return -toNumber(evaluate(node.x!, bb));
    case "not":
      return toTernary(evaluate(node.x!, bb)).not();

    // --- Arithmetic ---
    case "add":
      return toNumber(evaluate(node.l!, bb)) + toNumber(evaluate(node.r!, bb));
    case "sub":
      return toNumber(evaluate(node.l!, bb)) - toNumber(evaluate(node.r!, bb));
    case "mul":
      return toNumber(evaluate(node.l!, bb)) * toNumber(evaluate(node.r!, bb));
    case "div": {
      const divisor = toNumber(evaluate(node.r!, bb));
      if (divisor === 0) return 0; // Safe division by zero
      return toNumber(evaluate(node.l!, bb)) / divisor;
    }
    case "mod": {
      const divisor = toNumber(evaluate(node.r!, bb));
      if (divisor === 0) return 0;
      return toNumber(evaluate(node.l!, bb)) % divisor;
    }

    // --- Comparison (always returns Ternary) ---
    case "eq":
      return compareEq(evaluate(node.l!, bb), evaluate(node.r!, bb));
    case "neq":
      return compareNeq(evaluate(node.l!, bb), evaluate(node.r!, bb));
    case "lt":
      return compare(evaluate(node.l!, bb), evaluate(node.r!, bb), (a, b) => a < b);
    case "le":
      return compare(evaluate(node.l!, bb), evaluate(node.r!, bb), (a, b) => a <= b);
    case "gt":
      return compare(evaluate(node.l!, bb), evaluate(node.r!, bb), (a, b) => a > b);
    case "ge":
      return compare(evaluate(node.l!, bb), evaluate(node.r!, bb), (a, b) => a >= b);

    // --- Logic (ternary algebra) ---
    case "and":
      return toTernary(evaluate(node.l!, bb)).and(toTernary(evaluate(node.r!, bb)));
    case "or":
      return toTernary(evaluate(node.l!, bb)).or(toTernary(evaluate(node.r!, bb)));
    case "xor":
      return toTernary(evaluate(node.l!, bb)).xor(toTernary(evaluate(node.r!, bb)));

    // --- Visit count ---
    case "visits": {
      if (!_visitProvider) return 0;
      return _visitProvider.getVisits(node.scene ?? "");
    }

    // --- Collection literals ---
    case "list":
      return (node.items ?? []).map((item) => evaluate(item, bb));
    case "dict": {
      const result: Record<string, EvalResult> = {};
      for (const entry of node.entries ?? []) {
        result[entry.key] = evaluate(entry.value, bb);
      }
      return result;
    }

    // --- Function call in expression ---
    case "call": {
      // Native time builtins resolve before user functions.
      const timeBuiltin = node.n ? TIME_BUILTINS[node.n] : undefined;
      if (timeBuiltin) {
        if (!_gameClock) {
          console.warn(`ExprEval: time builtin "${node.n}" used but no calendar is configured`);
          return U;
        }
        const argv = (node.args ?? []).map((a) => evaluate(a, bb));
        return timeBuiltin(_gameClock, argv);
      }
      // Native inventory builtins also resolve before user functions.
      const invBuiltin = node.n ? INVENTORY_BUILTINS[node.n] : undefined;
      if (invBuiltin) {
        const argv = (node.args ?? []).map((a) => evaluate(a, bb));
        return invBuiltin(bb, argv);
      }
      if (!_functionProvider) {
        console.warn(`ExprEval: no function provider for call "${node.n}"`);
        return U;
      }
      const fnDef = _functionProvider.functions.find((f) => f.name === node.n);
      if (!fnDef) {
        console.warn(`ExprEval: function "${node.n}" not found`);
        return U;
      }
      if (!fnDef.body_ast) {
        console.warn(`ExprEval: function "${node.n}" has no evaluable body`);
        return U;
      }
      const argValues = (node.args ?? []).map((a) => evaluate(a, bb));
      callFunctionImpl(bb, fnDef, argValues);
      return untagValue(bb.get(fnDef.dest));
    }

    default:
      console.warn(`ExprEval: unknown node kind "${node.kind}"`);
      return U;
  }
}

/**
 * Resolve a template string against the Blackboard.
 *
 * Replaces `{expr}` placeholders using the bindings map (pre-parsed ASTs)
 * or by looking up simple variable names directly.
 */
export function resolveTemplate(
  template: string,
  bindings: Record<string, AstNode> | undefined,
  bb: Blackboard,
): string {
  return template.replace(/\{([^}]+)\}/g, (_match, expr: string) => {
    // Try bindings first (pre-parsed AST)
    if (bindings && bindings[expr]) {
      const result = evaluate(bindings[expr], bb);
      return formatValue(result);
    }
    // Fall back to simple variable lookup
    const tv = bb.get(expr);
    return formatValue(untagValue(tv));
  });
}

function formatValue(v: EvalResult): string {
  if (v instanceof Ternary) return v.toString();
  if (Array.isArray(v) || (v !== null && typeof v === "object")) return JSON.stringify(v);
  return String(v);
}
